import {
  Box,
  CircularProgress,
  Divider,
  MenuItem,
  Select,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { DateTime } from 'luxon';
import { useEffect, useState } from 'react';
import { toast } from 'material-react-toastify';
import { CreditCardForm } from '../../interfaces/Checkout/CreditCardForm';
import { PaymentForm } from '../../interfaces/Checkout/PaymentForm';
import { validatePayment } from './utils';
import { CardInfo } from '../../interfaces/Checkout/CardInfo';
import { useDebounce } from '../../hooks/useDebounce';
import { getCardInfo } from '../../api/CardInfo';
import { InstallmentTable } from './InstallmentTable';

type CreditCardContentProps = {
  form: PaymentForm;
  onChange: (newForm: CreditCardForm) => void;
  totalAmount: number;
};

export const CreditCardContent = ({
  form,
  onChange,
  totalAmount,
}: CreditCardContentProps) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('lg'));

  const [loadingCardInfo, setLoadingCardInfo] = useState(false);
  const [cardInfo, setCardInfo] = useState<CardInfo | undefined>(undefined);

  const debouncedCardNumber = useDebounce(form.creditCardForm.cardNumber, 300);

  useEffect(() => {
    if (debouncedCardNumber.length >= 15) {
      setLoadingCardInfo(true);
      setCardInfo(undefined);
      getCardInfo(debouncedCardNumber)
        .then(setCardInfo)
        .catch((err) => toast.error(err.message))
        .finally(() => setLoadingCardInfo(false));
    }
  }, [debouncedCardNumber]);

  const errors = validatePayment(form);

  const currentYear = DateTime.now().year;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: isSmallScreen ? 'column' : 'row',
        gap: 2,
        width: '100%',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: isSmallScreen ? '100%' : undefined,
          flex: isSmallScreen ? undefined : '1 1 0',
          gap: 2,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5,
            width: '100%',
          }}
        >
          <Typography>Kart Numarası</Typography>

          <TextField
            size='small'
            fullWidth
            value={form.creditCardForm.cardNumber
              .replace(/(.{4})/g, '$1 ')
              .trim()}
            onChange={(e) => {
              const rawValue = e.target.value.replace(/\D/g, ''); // Remove non-digits
              onChange({
                ...form.creditCardForm,
                cardNumber: rawValue.slice(0, 16),
                instalment: 1,
              });
            }}
            error={!!errors.cardNumber}
            helperText={errors.cardNumber}
          />
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5,
            width: '100%',
          }}
        >
          <Typography>İsim Soyisim</Typography>

          <TextField
            size='small'
            fullWidth
            value={form.creditCardForm.cardOwner}
            onChange={(e) => {
              onChange({
                ...form.creditCardForm,
                cardOwner: e.target.value,
              });
            }}
            error={!!errors.cardOwner}
            helperText={errors.cardOwner}
          />
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            gap: 1.5,
            justifyContent: 'space-between',
            flexWrap: 'wrap',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5,
            }}
          >
            <Typography>Son Kullanma Tarihi</Typography>

            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                gap: 1.5,
                width: '100%',
              }}
            >
              <Select
                size='small'
                value={form.creditCardForm.expiryMonth}
                required
                onChange={(e) =>
                  onChange({
                    ...form.creditCardForm,
                    expiryMonth: Number(e.target.value),
                  })
                }
                sx={{ width: 100 }}
              >
                {Array.from({ length: 12 }, (_, i) => i + 1).map((x) => (
                  <MenuItem key={x} value={x}>
                    {String(x).padStart(2, '0')}
                  </MenuItem>
                ))}
              </Select>

              <Select
                size='small'
                value={form.creditCardForm.expiryYear}
                required
                error={!!errors.expiryYear}
                onChange={(e) =>
                  onChange({
                    ...form.creditCardForm,
                    expiryYear: Number(e.target.value),
                  })
                }
                sx={{ width: 100 }}
              >
                {Array.from({ length: 35 }, (_, i) => currentYear + i).map(
                  (x) => (
                    <MenuItem key={x} value={x}>
                      {x}
                    </MenuItem>
                  ),
                )}
              </Select>
            </Box>
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5,
              flex: '1 1 0',
              minWidth: 100,
              maxWidth: 200,
            }}
          >
            <Typography>CVV</Typography>

            <TextField
              size='small'
              value={form.creditCardForm.cvc}
              onChange={(e) => {
                onChange({
                  ...form.creditCardForm,
                  cvc: e.target.value,
                });
              }}
              error={!!errors.cvc}
              helperText={errors.cvc}
              fullWidth
            />
          </Box>
        </Box>
      </Box>

      <Divider
        orientation={isSmallScreen ? 'horizontal' : 'vertical'}
        flexItem
      />

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: isSmallScreen ? '100%' : undefined,
          flex: isSmallScreen ? undefined : '1 1 0',
          gap: 1,
        }}
      >
        <Typography sx={{ fontWeight: 600 }}>Taksit Seçenekleri</Typography>

        <Divider />

        {loadingCardInfo && (
          <CircularProgress
            color='primary'
            sx={{ alignSelf: 'center', mt: 2 }}
          />
        )}

        {!loadingCardInfo && !cardInfo && (
          <Typography sx={{ fontSize: '14px' }}>
            Taksit seçeneklerini görüntülemek için kart numaranızı giriniz
          </Typography>
        )}

        {!!cardInfo && (
          <InstallmentTable
            cardInfo={cardInfo}
            totalAmount={totalAmount}
            selectedInstallment={form.creditCardForm.instalment}
            onSelect={(installment) =>
              onChange({ ...form.creditCardForm, instalment: installment })
            }
          />
        )}
      </Box>
    </Box>
  );
};
